// A user's change history, as returned by GET /users/customers/{mobile} under
// `history`: one event per API call, each listing the fields it changed.
//
// Verifications and KYC-flow steps are changes too: ticking a field is a change of
// "<field>_verified" ("Not verified" -> "Verified"); the customer's response a
// change of "kyc_status" (plus "customer_remarks").

export interface HistoryChange {
    field: string;
    old_value: string | null;
    new_value: string | null;
}

export interface HistoryEvent {
    by: string | null;
    by_name: string | null;
    by_role: string | null;
    timestamp: string;
    changes: HistoryChange[];
}

export const VERIFIED_SUFFIX = '_verified';

export const parseUserHistory = (user: any): HistoryEvent[] => {
    const list = user?.history;
    return Array.isArray(list) ? list.filter((e: any) => e && Array.isArray(e.changes)) : [];
};

// Per-field view for screens that ask "what last happened to field X" (the Verify
// KYC panels, nominee verification, the customer KYC page's notes). Reads the
// stored `kyc_history` so KYC-flow details survive (verify `value` snapshots,
// correction steps); update events in it are expanded into one "edit" per field.
export interface FieldHistoryEntry {
    action: string;
    field?: string;
    old_value?: unknown;
    new_value?: unknown;
    value?: unknown;
    by?: string | null;
    by_name?: string | null;
    by_role?: string | null;
    remarks?: string | null;
    timestamp?: string;
    [key: string]: unknown;
}

export const parseFieldHistory = (kycHistory: unknown): FieldHistoryEntry[] => {
    let list: unknown = kycHistory;
    if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch { return []; }
    }
    if (!Array.isArray(list)) return [];
    return list.flatMap((e: any): FieldHistoryEntry[] => {
        if (!e || typeof e !== 'object') return [];
        if (Array.isArray(e.changes)) {
            return e.changes.map((c: HistoryChange) => ({
                action: 'edit', field: c.field, old_value: c.old_value, new_value: c.new_value,
                by: e.by, by_name: e.by_name, by_role: e.by_role, timestamp: e.timestamp,
            }));
        }
        return typeof e.action === 'string' ? [e] : [];
    });
};
